import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutGrid,
  UsersRound,
  Download,
  AppWindow,
  Database,
  ShoppingCart,
  ReceiptText,
  ClipboardList,
  CircleQuestionMark,
  ChevronRight,
  X,
} from 'lucide-react';
import { useUserStore } from '../../../features/auth/store/userStore';
import { useSidebarVisibilityStore } from '../../../shared/store/sidebarVisibilityStore';
import { requestNavigation } from '../../../shared/store/navigationBlockStore';
import { ROLES, hasRole } from '../../../shared/constants/roles';
import { FirmSelector } from './FirmSelector';

// `visibilityKey` maps an item to its section in the sidebar visibility API
// response when the two names differ; items without it use their `id`.
const NAVIGATION_ITEMS = [
  // Only menu item in Stage 1 (new signup); the BE auto-hides it once an active subscription exists
  { id: 'checkout', label: 'Checkout', icon: ShoppingCart, path: '/checkout' },
  { id: 'dashboard', label: 'Dashboard', icon: LayoutGrid, path: '/dashboard', allowedRoles: [ROLES.ACCOUNT_OWNER, ROLES.BILLING_ADMIN, ROLES.LICENSE_ADMIN, ROLES.REGISTERED_CONTACT] },
  { id: 'subscriptions', label: 'Subscriptions', icon: AppWindow, path: '/subscriptions', allowedRoles: [ROLES.ACCOUNT_OWNER, ROLES.BILLING_ADMIN, ROLES.LICENSE_ADMIN] },
  { id: 'billing', label: 'Invoices', icon: ReceiptText, path: '/billing', visibilityKey: 'invoices', allowedRoles: [ROLES.ACCOUNT_OWNER, ROLES.BILLING_ADMIN] },
  { id: 'quotes', label: 'Quotes', icon: ClipboardList, path: '/quotes', visibilityKey: 'quotations', allowedRoles: [ROLES.ACCOUNT_OWNER, ROLES.BILLING_ADMIN] },
  { id: 'team', label: 'Users & Contacts', icon: UsersRound, path: '/team', visibilityKey: 'users', allowedRoles: [ROLES.ACCOUNT_OWNER, ROLES.BILLING_ADMIN, ROLES.LICENSE_ADMIN, ROLES.REGISTERED_CONTACT] },
  { id: 'downloads', label: 'Product Downloads & Links', icon: Download, path: '/downloads', allowedRoles: [ROLES.ACCOUNT_OWNER, ROLES.BILLING_ADMIN, ROLES.LICENSE_ADMIN, ROLES.REGISTERED_CONTACT] },
  { id: 'datanet', label: 'DataNet', icon: Database, path: '/datanet', allowedRoles: [ROLES.ACCOUNT_OWNER, ROLES.BILLING_ADMIN, ROLES.LICENSE_ADMIN, ROLES.REGISTERED_CONTACT] },
  // Last in the rail, as drawn. Gated on the `support` section of the visibility API like every
  // other item, so it renders only once that section is served and reports visible.
  { id: 'support', label: 'Contact Support', icon: CircleQuestionMark, path: '/contact-support', allowedRoles: [ROLES.ACCOUNT_OWNER, ROLES.BILLING_ADMIN, ROLES.LICENSE_ADMIN, ROLES.REGISTERED_CONTACT] },
];

export function AppSidebar({ isCollapsed, onToggleCollapse, isMobile = false, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { roles } = useUserStore();
  const { error, isItemVisible } = useSidebarVisibilityStore();

  const handleNavigation = (path) => {
    // A guarded page can veto this. Sidebar buttons navigate imperatively, so neither
    // beforeunload nor popstate fires — without this ask, a pending unpaid invoice could be
    // abandoned from here with no warning. The guard owns the dialog and completes the
    // navigation itself if the user chooses to leave.
    if (requestNavigation({ path })) return;
    navigate(path);
    // Close mobile sidebar after navigation
    if (isMobile && onClose) {
      onClose();
    }
  };

  if (error) {
    console.warn('Sidebar visibility error, using fallback:', error);
  }

  const visibleItems = NAVIGATION_ITEMS.filter((item) => {
    /* istanbul ignore next -- no NAVIGATION_ITEMS entry sets `hidden`; kept as a defensive guard */
    if (item.hidden) return false;

    // DataNet needs no special case: the visibility API already answers it from the firm's DataNet
    // firm_groups row (plus active-subscription + paid-invoice), the same source the Users and
    // Downloads pages use. Gating additionally on profile.hasDataNetSubscription — read from HaloPSA
    // licences and only loaded at bootstrap — kept the item hidden after a purchase until a manual
    // page refresh, because the payment flow refreshes this store but not the profile.

    // Lifecycle-stage visibility from the sidebar visibility API
    // TEMP: Skip visibility check for Quotes (for testing)
    if (item.id !== 'quotes' && !isItemVisible(item.visibilityKey || item.id)) return false;

    // Role-based filtering applies on top of lifecycle visibility
    const allowedRoles = Array.isArray(item.allowedRoles) ? item.allowedRoles : null;
    if (!allowedRoles) return true;

    return allowedRoles.some((role) => hasRole(roles, role));
  });

  const widthClass = isCollapsed && !isMobile ? 'w-16' : 'w-[270px]';

  return (
    <aside
      className={`bg-nav-bg border-r border-chrome-edge flex flex-col h-full transition-all duration-300 shrink-0 relative ${widthClass}`}
      data-testid="app-sidebar"
    >
      {/* Collapse toggle. Straddles the rail's right edge, so it reads as a handle on the sidebar
          rather than a control floating in the page. `z-30` keeps it above <main>, which follows it
          in the DOM and paints an opaque background over anything unpositioned — and above the
          sticky page headers inside it (z-20, see shared/lib/pageHeader), which would otherwise
          cover the half of the circle that overhangs the page.
          Wears the rail's own palette, --nav-bg down to the hover — it is part of the sidebar, not
          of the page it overhangs. The ring is --nav-tile-border (the hairline the firm tile
          inside the rail already uses) rather than --chrome-edge, which is transparent in light and
          would leave the half of the circle over the page with no edge at all.
          Desktop only: the drawer is dismissed with its own close button, not collapsed. */}
      {!isMobile && (
        <button
          type="button"
          onClick={onToggleCollapse}
          className="absolute -right-3 top-6 z-30 flex h-6 w-6 items-center justify-center rounded-full border border-nav-tile-border bg-nav-bg text-nav-fg shadow-md transition-colors hover:bg-nav-item-hover cursor-pointer"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!isCollapsed}
          aria-controls="app-sidebar-nav"
          data-testid="app-sidebar-collapse-toggle"
        >
          {/* One icon rotated rather than two swapped, so the arrow turns over the same 300ms the
              rail takes to change width. */}
          <ChevronRight
            className={`w-4 h-4 transition-transform duration-300 ${isCollapsed ? '' : 'rotate-180'}`}
          />
        </button>
      )}

      {/* Mobile Header with Close Button */}
      {isMobile && (
        <div className="flex items-center justify-end p-3 border-b border-nav-border">
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-nav-item-hover transition-colors cursor-pointer"
            aria-label="Close menu"
            data-testid="app-sidebar-close-button"
          >
            <X className="w-5 h-5 text-nav-fg" />
          </button>
        </div>
      )}

      {/* Firm Selector - shown for both desktop and mobile */}
      <FirmSelector isCollapsed={isMobile ? false : isCollapsed} isMobile={isMobile} />

      {/* Navigation */}
      <nav id="app-sidebar-nav" className="flex-1 p-3 space-y-1 overflow-y-auto overflow-x-hidden sidebar-inner" data-testid="app-sidebar-nav">
        {visibleItems.map((item) => {
          const Icon = item.icon;
          const isActive =
            location.pathname === item.path ||
            location.pathname.startsWith(`${item.path}/`);

          return (
            <div key={item.id} className="relative group" data-testid={`app-sidebar-nav-item-wrapper-${item.id}`}>
              <button
                // Explicit tabIndex: Safari leaves buttons out of plain-Tab order unless they carry
                // one (other browsers already include them, so this is a no-op there). Pending a
                // check on Safari; if it has no effect, drop it.
                tabIndex={0}
                onClick={() => handleNavigation(item.path)}
                // Collapsed, the button is an unlabelled icon, and the rail now stays collapsed
                // until the user says otherwise — hovering no longer reveals the label. `title`
                // gives that back as a tooltip, `aria-label` gives screen readers a name either
                // way. Both are unconditional: expanded, they simply repeat the visible text.
                title={item.label}
                aria-label={item.label}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-md transition-all cursor-pointer font-medium text-left ${
                  isActive
                    ? `bg-nav-item-active text-nav-fg`
                    : `text-nav-label hover:bg-nav-item-hover hover:text-nav-fg`
                }`}
                data-testid={`app-sidebar-nav-item-${item.id}`}
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-nav-fg' : 'text-nav-icon'}`} />
                {(isMobile || !isCollapsed) && (
                  <span className={`text-sm ${isActive ? 'font-semibold' : 'font-medium'} leading-tight flex-1 whitespace-nowrap`}>
                    {item.label}
                  </span>
                )}
              </button>
            </div>
          );
        })}
      </nav>

      {/* Copyright */}
      {(isMobile || !isCollapsed) && (
        <div className="p-4 bg-nav-footer border-t border-nav-footer-line" data-testid="app-sidebar-copyright">
          <p className="text-xs text-nav-footer-fg text-center font-medium">
            © 2026 Leimberg, LeClair &amp; Lackner, Inc.
          </p>
        </div>
      )}
    </aside>
  );
}
